import React, { createContext, ReactNode, useMemo } from 'react';
import { useAssistantAccess } from './hooks/useAssistantAccess';
import { useAssistantChat } from './hooks/useAssistantChat';
import { useAssistantLeadSelection } from './hooks/useAssistantLeadSelection';
import { useAssistantScope } from './hooks/useAssistantScope';
import { useWorkingLeads } from './hooks/useWorkingLeads';
import { TAssistantContextValue } from './types';

export const AssistantContext = createContext<TAssistantContextValue | null>(
    null
);

interface AssistantProviderProps {
    children: ReactNode;
}

/**
 * Business assistant state: selected lead, access, active chat, working leads
 * and the chat itself.
 */
export const AssistantProvider = ({ children }: AssistantProviderProps) => {
    const { availableLeadId, selectedLead } = useAssistantLeadSelection();
    const { activeScope, contextLeadId, scopeKey, scopeKeyRef, setChatScope } =
        useAssistantScope(availableLeadId);
    const {
        groupId,
        activeStoreId,
        assistantContextLoading,
        canUseAssistant,
        assistantVisible,
    } = useAssistantAccess({ selectedLead, contextLeadId });
    const workingLeads = useWorkingLeads();
    const chat = useAssistantChat({
        scopeKey,
        scopeKeyRef,
        contextLeadId,
        activeStoreId,
        canUseAssistant,
    });

    const value = useMemo<TAssistantContextValue>(
        () => ({
            context: {
                scope: activeScope,
                leadId: contextLeadId,
                groupId,
                storeId: activeStoreId,
            },
            assistantVisible,
            canUseAssistant,
            assistantContextLoading,
            selectedLead,
            setChatScope,
            ...workingLeads,
            ...chat,
        }),
        [
            activeScope,
            activeStoreId,
            assistantContextLoading,
            assistantVisible,
            canUseAssistant,
            chat,
            contextLeadId,
            groupId,
            selectedLead,
            setChatScope,
            workingLeads,
        ]
    );

    return (
        <AssistantContext.Provider value={value}>
            {children}
        </AssistantContext.Provider>
    );
};
